import { useState } from 'react';
import { ATS_CONSTANTS } from './constants';
import ErrorModal from '../../components/Modals/ErrorModal';
import { GeneratedOffer } from './types';

interface OfferPreviewProps {
  offersPreview: Array<{
    candidateId: string;
    offerLetterHtml: string;
    offerDetails: any;
  }>;
  mode: string;
  candidates: any[];
  onBack: () => void;
  onGenerateAndSave: (generatedOffers: GeneratedOffer[]) => void;
}

const OfferPreview: React.FC<OfferPreviewProps> = ({
  offersPreview,
  mode,
  candidates,
  onBack,
  onGenerateAndSave,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: '',
  });

  const generatePdf = async (html: string, filename: string, output: 'blob' | 'save' = 'save') => {
    // @ts-ignore
    const { default: html2pdf } = await import('html2pdf.js');

    const element = document.createElement('div');
    element.style.padding = '40px';
    element.style.backgroundColor = '#fff';
    element.style.width = '190mm'; // Adjusted for 10mm margins on A4 (210mm total)
    element.style.boxSizing = 'border-box';
    element.style.margin = '0'; // Normalize browser defaults
    element.innerHTML = html;

    const worker = html2pdf()
      .set({
        margin: 10,
        filename,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(element);

    if (output === 'blob') {
      return await worker.outputPdf('blob');
    } else {
      return await worker.save();
    }
  };

  const handleDownloadPreview = async () => {
    setDownloading(true);
    try {
      const combinedHtml = offersPreview.map(p => p.offerLetterHtml).join('<div style="page-break-after: always;"></div>');
      const filename =
        mode === ATS_CONSTANTS.OFFER_MODES.SINGLE && candidates[0]?.name
          ? `offer_${candidates[0].name.replace(/\s+/g, '_').toLowerCase()}.pdf`
          : `offer_preview_${Date.now()}.pdf`;

      await generatePdf(combinedHtml, filename, 'save');
    } catch (error) {
      console.error('Error downloading preview:', error);
      setErrorModal({ isOpen: true, message: 'Failed to download preview' });
    } finally {
      setDownloading(false);
    }
  };

  const handleGenerateAndSave = async () => {
    setGenerating(true);
    try {
      const generatedOffers: GeneratedOffer[] = [];

      for (const preview of offersPreview) {
        const candidate = candidates.find((c) => c.id === preview.candidateId);
        const candidateName = candidate?.name || 'candidate';
        const orgName = preview.offerDetails?.org_name || preview.offerDetails?.orgName || 'offers';

        const cleanOrgName = orgName.replace(/\s+/g, '_').toLowerCase();
        const cleanCandidateName = candidateName.replace(/\s+/g, '_').toLowerCase();
        const filename = `${cleanOrgName}/${cleanCandidateName}_${preview.candidateId}.pdf`;

        const blob = await generatePdf(preview.offerLetterHtml, filename, 'blob');

        const feeAmount = preview.offerDetails?.placementFee ? Number(preview.offerDetails.placementFee) : undefined;
        const dateOfJoining = preview.offerDetails?.joining_date || preview.offerDetails?.joiningDate || undefined;

        generatedOffers.push({
          applicationId: preview.candidateId,
          blob,
          filename,
          feeAmount,
          dateOfJoining,
        });
      }

      onGenerateAndSave(generatedOffers);
    } catch (error) {
      console.error('Error generating PDFs:', error);
      setErrorModal({ isOpen: true, message: 'Failed to generate offer letter PDFs' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          marginBottom: '20px',
        }}
      >
        {offersPreview.map(({ candidateId, offerLetterHtml }, index) => (
          <div
            key={candidateId || `preview-${index}`}
            data-testid="offer-preview-item"
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '24px',
              backgroundColor: '#fff',
              minHeight: '320px',
              overflow: 'visible',
            }}
            dangerouslySetInnerHTML={{ __html: offerLetterHtml }}
          />
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <button
          onClick={handleDownloadPreview}
          disabled={downloading || generating}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '700',
            fontFamily: "'Outfit', sans-serif",
            opacity: downloading || generating ? 0.7 : 1,
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          data-testid="offer-preview-download"
        >
          {downloading ? 'Downloading...' : 'Download Preview as PDF'}
        </button>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onBack}
            disabled={generating}
            style={{
              padding: '12px 24px',
              backgroundColor: 'white',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '700',
              fontFamily: "'Outfit', sans-serif",
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: generating ? 0.7 : 1,
            }}
            data-testid="offer-preview-back"
          >
            Back
          </button>
          <button
            onClick={handleGenerateAndSave}
            disabled={generating}
            style={{
              padding: '12px 24px',
              background: 'var(--ss-primary-gradient)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '700',
              fontFamily: "'Outfit', sans-serif",
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(0, 112, 192, 0.2)',
              opacity: generating ? 0.7 : 1,
            }}
            data-testid="offer-preview-generate"
          >
            {generating ? 'Generating...' : 'Generate & Save'}
          </button>
        </div>
      </div>
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
      />
    </div>
  );
};

export default OfferPreview;
